/* eslint-disable no-unused-expressions */
import { useRef } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useIntersectionObserver } from '@react-hookz/web';

import { screen } from '@/components/common/breakpoints';
import { bodyLarge, labelLarge } from '@/styles/typography';

import type { ReactNode } from 'react';

export interface FormContainerProps {
  header?: {
    title: string;
    description?: string;
  };
  children: ReactNode;
}

export const FormContainer = ({ header, children }: FormContainerProps) => {
  const afterFormRef = useRef<HTMLDivElement>();
  const beforeFormRef = useRef<HTMLDivElement>();
  const formRef = useRef<HTMLDivElement>();

  const headingIntersection = useIntersectionObserver(beforeFormRef, {
    threshold: [1],
  });
  const afterFormIntersection = useIntersectionObserver(afterFormRef, {
    root: formRef,
    threshold: [1],
  });

  const formOrPageIsScrolled =
    headingIntersection?.target && !headingIntersection?.isIntersecting;
  const formIsScrollMax = afterFormIntersection?.isIntersecting;

  return (
    <Container>
      <FormWrapper ref={formRef}>
        <Detector ref={beforeFormRef} />
        {Boolean(header?.title) && (
          <Header
            formOrPageIsScrolled={formOrPageIsScrolled}
            descriptionExists={Boolean(header.description)}
          >
            <HeaderTitle>{header.title}</HeaderTitle>
            {header.description && (
              <HeaderDescription hideDescription={formOrPageIsScrolled}>
                {header.description}
              </HeaderDescription>
            )}
          </Header>
        )}
        <ContentContainer isContentScrollAtMax={formIsScrollMax}>
          {children}
        </ContentContainer>
        <Detector ref={afterFormRef} />
      </FormWrapper>
    </Container>
  );
};

const Detector = styled.div`
  position: relative;
  height: 1px;
`;

const Container = styled.div`
  &.input-focus {
    position: fixed;
    top: 0;
    height: 100vh;
    display: block;
  }

  ${screen.md} {
    display: flex;
    flex-direction: column;
    height: 100%;
    flex: 1;
  }
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--border-warm-subtle);
  width: 100%;
  background-color: var(--background-subtle);

  ${screen.md} {
    overflow-y: scroll;
    border-radius: var(--radius-m);
    border-top: none;
    align-self: center;
    margin: 0;
  }
`;

const Header = styled.div<{
  formOrPageIsScrolled: boolean;
  descriptionExists: boolean;
}>`
  --bottom-spacing: ${({ descriptionExists }) =>
    descriptionExists ? 'var(--spacing-500)' : 'var(--spacing-small)'};

  width: 100%;
  z-index: 20;
  background: var(--background-subtle);

  display: flex;
  flex-direction: column;
  align-items: center;

  border-bottom: ${({ formOrPageIsScrolled }) =>
    formOrPageIsScrolled ? '1px solid var(--colour-border-subtle)' : 'none'};

  padding-block: var(--spacing-400) var(--spacing-x-small);
  padding-inline: var(--spacing-x-small);

  ${({ formOrPageIsScrolled }) =>
    formOrPageIsScrolled &&
    css`
      padding-block: var(--spacing-small) var(--spacing-x-small) !important;
      margin-bottom: var(--bottom-spacing);
    `}

  ${screen.md} {
    position: sticky;
    top: 0;
    --bottom-spacing: ${({ descriptionExists }) =>
      descriptionExists ? 'var(--spacing-600)' : 'var(--spacing-300)'};

    padding-block: var(--spacing-500) var(--spacing-x-small);
  }
`;

const HeaderTitle = styled.span`
  ${labelLarge}
  padding-bottom: var(--spacing-xxx-small);
  color: var(--text-strong);
`;

const HeaderDescription = styled.span<{
  hideDescription: boolean;
}>`
  ${bodyLarge}
  color: var(--text-strong);

  ${({ hideDescription }) =>
    hideDescription &&
    css`
      display: none;
    `};
`;

const ContentContainer = styled.div<{
  isContentScrollAtMax: boolean;
}>`
  form {
    padding: var(--spacing-300) var(--spacing-250) 0 var(--spacing-250);
  }

  ${screen.md} {
    margin-top: 0;
    form {
      padding: var(--spacing-300);
    }

    .footer-container {
      border-top: ${({ isContentScrollAtMax }) =>
        isContentScrollAtMax
          ? 'none'
          : '1px solid var(--colour-border-subtle)'};
    }
  }
`;
